import React from 'react';

import { Chip, MenuItem } from 'components';

import { getEventChipColor } from './AuditDetailModal';

import { i18n, useTranslation } from 'third-party';

import { ListFilter } from 'components/Table/Filtering/ListFilter';
import { ColumnFilteringProps, TableColumn } from 'components/Table/types';

import { formatDateTimeWithTimezone, moneyFormatter } from 'utils/app';
import { getEnumValues } from 'utils/helper';

import { APIAudit, APIUser } from 'types/api';
import { APIEntityType, APIEventType } from 'types/backend';

export const getAuditDescription = (item: APIAudit): string => {
  const value = item.data;

  if (item.entityType === APIEntityType.Payee) {
    return String(
      i18n.t('auditList.payeeDescription', {
        name: value?.[0] || '',
        email: value?.[1] || '',
        phone: value?.[2] || '',
        status: value?.[3] || '',
      }),
    );
  }

  if (item.entityType === APIEntityType.Payment) {
    return String(
      i18n.t('auditList.paymentDescription', {
        amount: moneyFormatter(value?.[0] || '0'),
        payee: value?.[1] || '',
        status: value?.[2] || '',
      }),
    );
  }

  if (item.entityType === APIEntityType.Bank) {
    return String(
      i18n.t('auditList.bankDescription', {
        bankName: value?.[0] || '',
        accountName: value?.[1] || '',
        routing: value?.[2] || '',
        account: value?.[3] || '',
        companyName: value?.[4] || '',
      }),
    );
  }

  if (item.entityType === APIEntityType.User) {
    return String(
      i18n.t('auditList.userDescription', {
        firstName: value?.[0] || '',
        lastName: value?.[1] || '',
        email: value?.[2] || '',
      }),
    );
  }

  if (item.entityType === APIEntityType.Settings) {
    return String(
      i18n.t('auditList.settingsDescription', {
        name: i18n.t('settings.' + value?.[0]) || '',
      }),
    );
  }

  if (item.entityType === APIEntityType.ApiClientToken) {
    return String(
      i18n.t('auditList.apiClientTokenDescription', {
        name: value?.[0] || '',
        isEnabled: value?.[1] || '',
      }),
    );
  }

  if (item.entityType === APIEntityType.BlankCheckRange) {
    return String(
      i18n.t('auditList.blankCheckDescription', {
        bankName: value?.[0] || '',
        from: value?.[1] || '',
        to: value?.[2] || '',
        signature: value?.[3] || '',
        printType: value?.[4] || '',
      }),
    );
  }

  return Array.isArray(value) ? value.join(', ') : String(value ?? '');
};

export const EventTypeFilter: React.FC<ColumnFilteringProps> = props => {
  const { t } = useTranslation();
  return (
    <ListFilter {...props}>
      <MenuItem key="all" value={undefined}>
        {t('common.all')}
      </MenuItem>
      {getEnumValues(APIEventType).map(status => (
        <MenuItem key={status} value={status}>
          {t(`audit.status.${status}`)}
        </MenuItem>
      ))}
    </ListFilter>
  );
};

export const EntityTypeFilter: React.FC<ColumnFilteringProps> = props => {
  const { t } = useTranslation();
  return (
    <ListFilter {...props}>
      <MenuItem key="all" value={undefined}>
        {t('common.all')}
      </MenuItem>
      {getEnumValues(APIEntityType).map(status => (
        <MenuItem key={status} value={status}>
          {t(`audit.entityType.${status}`)}
        </MenuItem>
      ))}
    </ListFilter>
  );
};

export const auditColumns = (
  timezoneValue: string,
): TableColumn<APIAudit>[] => [
  {
    dataId: 'eventType',
    label: i18n.t('auditList.column.eventType'),
    minWidth: 120,
    width: 160,
    hidden: false,
    sortable: true,
    resizable: true,
    customFilter: EventTypeFilter,
    formatter: (value: any) => {
      const label = String(i18n.t('audit.status.' + value));
      return {
        value: label,
        element: (
          <Chip label={label} color={getEventChipColor(value)} size="small" />
        ),
      };
    },
  },
  {
    dataId: 'data',
    label: i18n.t('auditList.column.description'),
    minWidth: 200,
    width: 400,
    hidden: false,
    sortable: false,
    resizable: true,
    filterType: 'string',
    formatter: (_value: string[], item: APIAudit) => ({
      value: getAuditDescription(item),
    }),
  },
  {
    dataId: 'entityType',
    label: i18n.t('auditList.column.entityType'),
    minWidth: 120,
    width: 140,
    hidden: false,
    sortable: true,
    resizable: true,
    customFilter: EntityTypeFilter,
    formatter: (value: any) => ({
      value: String(i18n.t(`audit.entityType.${value}`)),
    }),
  },
  {
    dataId: 'entityId',
    label: i18n.t('auditList.column.entityId'),
    minWidth: 120,
    width: 200,
    hidden: true,
    sortable: true,
    resizable: true,
    filterType: 'string',
  },
  {
    dataId: 'createdAt',
    label: i18n.t('auditList.column.createdAt'),
    minWidth: 120,
    width: 200,
    hidden: false,
    sortable: true,
    resizable: true,
    filterType: 'date',
    formatter: value => ({
      value: formatDateTimeWithTimezone(value as string, timezoneValue),
    }),
  },
  {
    dataId: 'createdBy',
    label: i18n.t('auditList.column.createdBy'),
    minWidth: 120,
    width: 200,
    hidden: false,
    sortable: true,
    resizable: true,
    filterType: 'string',
    formatter: (value: APIUser, item: APIAudit) => ({
      value: value
        ? `${value.firstName} ${value.lastName}${
            item.createdByToken
              ? ` via api token "${item.createdByToken.name}"`
              : ''
          }`
        : '—',
    }),
  },
];
